import React, { useEffect, useMemo, useState } from "react";
import { Box, Switch, TextField } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { StepProps } from "./StepComponents";
import EventSettingCard from "./EventSettingCard";
import { composerEventDetailFieldSx } from "./composerBorderStyles";
import {
  combinePublishAtParts,
  parsePublishAtParts,
} from "../../utils/noticeScheduling";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../utils/eventDateDisplay";

const eventDetailFieldSx = composerEventDetailFieldSx;

type EventRsvpSettingCardProps = {
  formData: Pick<StepProps["formData"], "rsvpEnabled" | "rsvpClosesAt">;
  errors: Pick<StepProps["errors"], "rsvpClosesAt">;
  handleChange: (event: { target: { name: string; value: any } }) => void;
  disabled?: boolean;
};

/** RSVP on an Event, with an optional deadline. Used by Add Event and Planner rows. */
const EventRsvpSettingCard: React.FC<EventRsvpSettingCardProps> = ({
  formData,
  errors,
  handleChange,
  disabled = false,
}) => {
  const rsvpEnabled = Boolean(formData.rsvpEnabled);

  const { date: rsvpDeadlineDate, time: rsvpDeadlineTime } = useMemo(
    () => parsePublishAtParts(formData.rsvpClosesAt),
    [formData.rsvpClosesAt],
  );

  // The picker keeps a half-typed date here; only a real date becomes the deadline.
  const [pickerDate, setPickerDate] = useState<Date | null>(rsvpDeadlineDate);
  useEffect(() => {
    setPickerDate(rsvpDeadlineDate);
  }, [rsvpDeadlineDate]);

  const updateRsvpClosesAt = (date: Date | null, time: string) => {
    setPickerDate(date);
    // Still being typed: not a date yet, or a year like "2" on the way to "2026".
    if (date && (Number.isNaN(date.getTime()) || date.getFullYear() < 1000)) return;
    const combined = combinePublishAtParts(date, time);
    const deadline = combined ? new Date(combined) : null;
    if (deadline && Number.isNaN(deadline.getTime())) return;
    handleChange({
      target: {
        name: "rsvpClosesAt",
        value: deadline ? deadline.toISOString() : "",
      },
    });
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <EventSettingCard
        icon={<GroupsOutlinedIcon fontSize="small" />}
        title="RSVPs"
        description="Allow people to respond to this event."
        titleTooltip="App Users will be able to let you know if they are attending the event"
        error={errors.rsvpClosesAt}
        control={
          <Switch
            checked={rsvpEnabled}
            onChange={(event) =>
              handleChange({
                target: { name: "rsvpEnabled", value: event.target.checked },
              })
            }
            color="primary"
            disabled={disabled}
            inputProps={{ "aria-label": "RSVPs" }}
          />
        }
      >
        {rsvpEnabled ? (
          <Box sx={{ pt: 1.5 }}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={EVENT_DATE_PICKER_LOCALE}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <DatePicker
                  label="RSVP deadline date (optional)"
                  format={EVENT_DATE_PICKER_FORMAT}
                  value={pickerDate}
                  minDate={new Date()}
                  disabled={disabled}
                  enableAccessibleFieldDOMStructure={false}
                  onChange={(date) =>
                    updateRsvpClosesAt(date, rsvpDeadlineTime || "23:59")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: eventDetailFieldSx,
                      error: !!errors.rsvpClosesAt,
                    },
                    popper: {
                      sx: {
                        zIndex: (theme) => theme.zIndex.modal + 4,
                      },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label="RSVP deadline time (optional)"
                  value={rsvpDeadlineTime || ""}
                  onChange={(event) =>
                    updateRsvpClosesAt(rsvpDeadlineDate, event.target.value)
                  }
                  error={!!errors.rsvpClosesAt}
                  InputLabelProps={{ shrink: true }}
                  disabled={disabled}
                  sx={eventDetailFieldSx}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        ) : null}
      </EventSettingCard>
    </Box>
  );
};

export default EventRsvpSettingCard;
